import { memo, useState } from "react";
import { TableItemId } from "../../../../../components/tables/TableWidget";
import { TableItemType } from "../../../../../components/tables/tableContext/TableContext";
import { useAppDispatch } from "../../../../../redux/hooks";
import ActionMenu, {
  ActionMenuItem,
} from "../../../../../components/actionMenu/ActionMenu";
import { UndeliveredAwbDataInterface } from "../../../../../redux/interfaces/expedition.interface";
import {
  fetchPrintAwbA4,
  fetchPrintAwbZebra,
  fetchUndeliveredAwb,
  postAssociateAwbToBin,
  postCancelAwb,
  postMarkIndividualPackages,
} from "../../../../../redux/api/expedition.api";
import {
  checkEndpointStatus,
  endpointStatus,
} from "../../../../../helpers/apiHelper";
import {
  openChangeDateUndeliveredAwbModal,
  setClientSelectedUndeliveredAwb,
  setSelectedUndeliveredAwbForChangingDate,
} from "../../../../../redux/reducer/expedition.reducer";

function UndeliveredAwbTableActionButtons({
  itemId,
  crudData,
  tableContext,
}: {
  itemId: TableItemId;
  tableItemData: TableItemType[];
  crudData: UndeliveredAwbDataInterface;
  tableContext?: any;
}) {
  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = useState(false);

  const handleAssociateAwb = async () => {
    const resultAction = await dispatch(
      postAssociateAwbToBin({
        params: {
          id_doctrans: itemId,
        },
      }),
    );

    if (
      checkEndpointStatus(resultAction, postAssociateAwbToBin) !==
      endpointStatus.pending
    ) {
      setIsLoading(false);
    }

    if (
      checkEndpointStatus(resultAction, postAssociateAwbToBin) ===
      endpointStatus.fulfilled
    ) {
      dispatch(
        fetchUndeliveredAwb({
          page: tableContext.tablePagination.page,
          perPage: tableContext.tablePagination.rowsPerPage,
          doc_trans: tableContext.tableSearch.doc_trans,
          pctlivrare: tableContext.tableSearch.pctlivrare,
          nodDestinatie: tableContext.tableFilter.nodDestinatie,
          status: tableContext.tableFilter.status,
        }),
      );
    }
  };

  const handlePrintAwbA4 = () => {
    if (itemId) {
      dispatch(
        fetchPrintAwbA4({
          idDocTrans: itemId,
        }),
      );
    }
  };

  const handlePrintAwbZebra = () => {
    if (itemId) {
      dispatch(
        fetchPrintAwbZebra({
          idDocTrans: itemId,
        }),
      );
    }
  };

  const handleSetSelectedClient = () => {
    dispatch(setClientSelectedUndeliveredAwb(crudData));
  };

  const handleChangePickingDate = () => {
    dispatch(setSelectedUndeliveredAwbForChangingDate(crudData));
    dispatch(openChangeDateUndeliveredAwbModal());
  };

  const handleCancelAwb = async () => {
    const resultAction = await dispatch(
      postCancelAwb({
        id_doctrans: crudData.id_doctrans,
      }),
    );

    if (
      checkEndpointStatus(resultAction, postCancelAwb) !==
      endpointStatus.pending
    ) {
      setIsLoading(false);
    }

    if (
      checkEndpointStatus(resultAction, postCancelAwb) ===
      endpointStatus.fulfilled
    ) {
      dispatch(
        fetchUndeliveredAwb({
          page: tableContext.tablePagination.page,
          perPage: tableContext.tablePagination.rowsPerPage,
          doc_trans: tableContext.tableSearch.doc_trans,
          pctlivrare: tableContext.tableSearch.pctlivrare,
          nodDestinatie: tableContext.tableFilter.nodDestinatie,
          status: tableContext.tableFilter.status,
        }),
      );
    }
  };

  const handleMarkIndividualPackages = async () => {
    const resultAction = await dispatch(
      postMarkIndividualPackages({
        id_doctrans: crudData.id_doctrans,
      }),
    );

    if (
      checkEndpointStatus(resultAction, postMarkIndividualPackages) !==
      endpointStatus.pending
    ) {
      setIsLoading(false);
    }

    if (
      checkEndpointStatus(resultAction, postMarkIndividualPackages) ===
      endpointStatus.fulfilled
    ) {
      dispatch(
        fetchUndeliveredAwb({
          page: tableContext.tablePagination.page,
          perPage: tableContext.tablePagination.rowsPerPage,
          doc_trans: tableContext.tableSearch.doc_trans,
          pctlivrare: tableContext.tableSearch.pctlivrare,
          nodDestinatie: tableContext.tableFilter.nodDestinatie,
          status: tableContext.tableFilter.status,
        }),
      );
    }
  };

  const items: ActionMenuItem[] = [
    {
      id: "associate-awb",
      label: "Asociere expeditie",
      icon: "ASSOCIATE_AWB",
      section: "manage",
      color: "primary",
      loading: isLoading,
      hidden: !crudData?.id_doctrans,
      tooltip:
        "Adauga AWB-ul la volumul de BIN-uri pentru nodul de destinatie.",
      withConfirmationModal: {
        modalTitle: "Asociere expeditie",
        modalSubtitle: `Doriti sa adaugati coletul la binurile de pe nodul ${crudData.nodDestinatie}?`,
        modalLeftButtonVariant: "outlined",
        modalRightButtonOnClick: handleAssociateAwb,
      },
    },
    {
      id: "mark-individual-packages",
      label: "Colete individuale",
      icon: "PACKAGES",
      section: "manage",
      color: "warning",
      loading: isLoading,
      hidden: crudData.status !== 0 && crudData.status !== 3,
      tooltip:
        "Marcheaza AWB-ul si toate coletele sale pentru livrare individuala.",
      withConfirmationModal: {
        modalTitle: "Colete cu livrare individuala",
        modalSubtitle: `Doriti sa marcati toate colete cu awb-ul ${crudData.doc_trans} ca fiind cu livrare individuala?`,
        modalLeftButtonVariant: "outlined",
        modalRightButtonOnClick: handleMarkIndividualPackages,
      },
    },
    {
      id: "change-working-point",
      label: "Schimbare punct lucru",
      icon: "CHANGE_WORKING_POINT",
      section: "manage",
      dividerBefore: true,
      tooltip: "Alege un alt punct de livrare pentru AWB.",
      onClick: handleSetSelectedClient,
    },
    {
      id: "change-picking-date",
      label: "Schimbare data ridicarii",
      icon: "CHANGE_DATE",
      section: "manage",
      tooltip: "Alege o noua data si ora pentru ridicarea acestei expeditii.",
      onClick: handleChangePickingDate,
    },
    {
      id: "print-a4",
      label: "Print A4",
      icon: "PRINT_A4",
      kind: "print",
      section: "browse",
      tooltip: "Deschide etichetele AWB in format PDF A4.",
      onClick: handlePrintAwbA4,
    },
    {
      id: "print-zebra",
      label: "Print Zebra",
      icon: "PRINT_ZEBRA",
      kind: "print",
      section: "browse",
      tooltip: "Pregateste etichetele AWB pentru imprimare Zebra.",
      onClick: handlePrintAwbZebra,
    },
    {
      id: "cancel-awb",
      label: "Anuleaza AWB",
      icon: "CANCEL",
      color: "error",
      section: "danger",
      dividerBefore: true,
      loading: isLoading,
      hidden: crudData.status !== 0,
      tooltip: "Anuleaza AWB-ul si toate coletele sale.",
      withConfirmationModal: {
        modalTitle: "Anuleaza AWB",
        modalSubtitle: `Doriti sa anulati awb-ul ${crudData.doc_trans}?`,
        modalLeftButtonVariant: "outlined",
        modalRightButtonOnClick: handleCancelAwb,
      },
    },
  ];

  return (
    <ActionMenu
      items={items}
      minItemsForMenu={2}
      title={`AWB ${crudData.doc_trans}`}
      ariaLabel={`Actiuni pentru AWB ${crudData.doc_trans}`}
    />
  );
}

export default memo(UndeliveredAwbTableActionButtons);
